/**
 * Section Layout Settings Configuration
 *
 * Registry mapping each SectionKind to its layout settings behavior,
 * plus a field group factory for the SectionLayoutSettings editor panel.
 *
 * Architecture: SECTION_KIND_SETTINGS allows per-kind overrides (hidden/forced fields).
 * Initially all settings are available for all section kinds.
 * Specific restrictions (e.g. ProductCatalog always auto-height) will be added later.
 */

import type { SectionKind, SectionLayout } from '@segmently/schema-models/v2';
import type { ListStyleSectionConfig } from '../types/listSection.types';
import { NUMBER_0_50_OPTIONS, NUMBER_0_100_OPTIONS, BOOLEAN_OPTIONS, HORIZONTAL_ALIGNMENT_OPTIONS } from './atomicFields';

// ── Per-field override types ────────────────────────────────────────────────

/** Describes how a specific SectionLayout field behaves for a given SectionKind */
export interface SectionLayoutFieldOverride {
  /** If true, the field is hidden from the editor entirely */
  hidden?: boolean;
  /** If set, the field is displayed in the editor but locked (non-editable) to this value */
  forcedValue?: unknown;
}

/** Settings descriptor for a SectionKind — maps layout field names to overrides */
export interface SectionKindLayoutSettings {
  overrides?: Partial<Record<keyof SectionLayout | string, SectionLayoutFieldOverride>>;
}

// ── Registry ────────────────────────────────────────────────────────────────

/**
 * Section-kind-aware settings registry.
 *
 * Initially all section kinds have empty overrides (all settings available).
 * Add concrete restrictions later, e.g.:
 *
 * ```ts
 * ProductCatalog: {
 *   overrides: {
 *     heightKind: { forcedValue: 'auto' },
 *   },
 * },
 * ```
 */
export const SECTION_KIND_SETTINGS: Record<SectionKind, SectionKindLayoutSettings> = {
  Text: {},
  Media: {},
  ProductCatalog: {},
  Button: {},
  OptionsList: {},
  SingleSelectionList: {},
  MultipleSelectionList: {},
  BulletList: {},
  CustomEmbed: {},
  Background: {},
  Header: {},
  Footer: {},
  PaywallHeader: {},
  TextField: {},
  EmptySpace: {},

  // Article block kinds (schema 3.2.0): article-only and authored through the CLI
  // (spec A15). No Studio overrides — the Studio palettes never offer them (articleKindsNotOffered.test.ts).
  // ArticleIndex (schema 3.3.0) is offered in the ARTICLE editor palette only (spec issue #16).
  ArticleBreadcrumb: {},
  ArticleShortAnswer: {},
  ArticleByline: {},
  ArticleToc: {},
  ArticleTakeaways: {},
  ArticleRelated: {},
  ArticleCta: {},
  ArticleFooter: {},
  ArticleIndex: {},
};

// ── Number options for flex (0–10) ──────────────────────────────────────────

const NUMBER_0_10_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

// ── Height value options for fixed mode (0–1000, step 10) ───────────────────

const NUMBER_0_1000_OPTIONS = Array.from({ length: 101 }, (_, i) => ({
  value: String(i * 10),
  label: String(i * 10),
}));

// ── Vertical alignment options ──────────────────────────────────────────────

const VERTICAL_ALIGNMENT_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'center', label: 'Center' },
  { value: 'bottom', label: 'Bottom' },
] as const;

// ── Height kind options ─────────────────────────────────────────────────────

const HEIGHT_KIND_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'fixed', label: 'Fixed (px)' },
  { value: 'percent', label: 'Percent (%)' },
] as const;

// ── Width kind options (v2.19.0) ────────────────────────────────────────────
// Exported for reuse by the screen-level "Default section layout" block
// (V2FlexibleSectionsSection gear panel).

export const WIDTH_KIND_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'fixed', label: 'Fixed (px)' },
  { value: 'percent', label: 'Percent (%)' },
] as const;

export const SECTION_SELF_ALIGNMENT_OPTIONS = [
  { value: 'start', label: 'Start' },
  { value: 'center', label: 'Center' },
  { value: 'end', label: 'End' },
  { value: 'stretch', label: 'Stretch' },
] as const;

// ── Field group factory ─────────────────────────────────────────────────────

/**
 * Creates the field group configuration for the SectionLayoutSettings panel.
 *
 * Returns 3 collapsible accordion sections:
 * 1. Size & Flex — heightKind, heightValue, flexGrow, flexShrink, scrollable
 * 2. Appearance — backgroundColor, cornerRadius, border width/color
 * 3. Spacing & Alignment — alignment + padding (PaddingElement)
 *
 * Field paths use `layout.` prefix, read via getValueByPath(section, path).
 */
export function createSectionLayoutFieldGroups(): ListStyleSectionConfig[] {
  return [
    // ── Size & Flex ───────────────────────────────────────────────────
    {
      id: 'sizeAndFlex',
      title: 'Size & Flex',
      defaultExpanded: false,
      fields: [
        {
          path: 'layout.heightKind',
          guideKey: 'screenedit-flexible-sections-height-mode',
          label: 'Height mode',
          type: 'select',
          options: [...HEIGHT_KIND_OPTIONS],
          defaultValue: 'auto',
          isNullable: false,
        },
        {
          path: 'layout.heightValue',
          guideKey: 'screenedit-flexible-sections-height-value',
          label: 'Height value',
          type: 'numberCombo',
          options: NUMBER_0_1000_OPTIONS,
          defaultValue: 100,
          defaultScrollValue: 100,
          isNullable: true,
        },
        {
          path: 'layout.flexGrow',
          guideKey: 'screenedit-flexible-sections-flex-grow',
          label: 'Flex grow',
          type: 'numberCombo',
          options: NUMBER_0_10_OPTIONS,
          defaultValue: 0,
          defaultScrollValue: 0,
          isNullable: false,
        },
        {
          path: 'layout.flexShrink',
          guideKey: 'screenedit-flexible-sections-flex-shrink',
          label: 'Flex shrink',
          type: 'numberCombo',
          options: NUMBER_0_10_OPTIONS,
          defaultValue: 1,
          defaultScrollValue: 1,
          isNullable: false,
        },
        {
          path: 'layout.scrollable',
          guideKey: 'screenedit-flexible-sections-scrollable',
          label: 'Scrollable',
          type: 'select',
          options: [...BOOLEAN_OPTIONS],
          defaultValue: 'false',
          isNullable: false,
        },
        {
          path: 'layout.zIndex',
          guideKey: 'screenedit-flexible-sections-z-index',
          label: 'Z-index',
          type: 'numberCombo',
          options: NUMBER_0_50_OPTIONS,
          defaultValue: 0,
          defaultScrollValue: 0,
          isNullable: true,
        },
      ],
    },

    // ── Width (v2.19.0) ───────────────────────────────────────────────
    {
      id: 'width',
      title: 'Width',
      defaultExpanded: false,
      fields: [
        {
          path: 'layout.widthKind',
          guideKey: 'screenedit-flexible-sections-width-mode',
          label: 'Width mode',
          type: 'select',
          options: [...WIDTH_KIND_OPTIONS],
          defaultValue: undefined,
          isNullable: true,
        },
        {
          path: 'layout.widthValue',
          guideKey: 'screenedit-flexible-sections-width-value',
          label: 'Width value',
          type: 'numberCombo',
          options: NUMBER_0_1000_OPTIONS,
          defaultValue: 100,
          defaultScrollValue: 100,
          isNullable: true,
        },
        {
          path: 'layout.maxWidth',
          guideKey: 'screenedit-flexible-sections-max-width',
          label: 'Max width (px)',
          type: 'numberCombo',
          options: NUMBER_0_1000_OPTIONS,
          defaultValue: undefined,
          defaultScrollValue: 480,
          isNullable: true,
        },
        {
          path: 'layout.minWidth',
          guideKey: 'screenedit-flexible-sections-min-width',
          label: 'Min width (px)',
          type: 'numberCombo',
          options: NUMBER_0_1000_OPTIONS,
          defaultValue: undefined,
          defaultScrollValue: 0,
          isNullable: true,
        },
        {
          path: 'layout.sectionHorizontalAlignment',
          guideKey: 'screenedit-flexible-sections-self-alignment',
          label: 'Self align',
          type: 'select',
          options: [...SECTION_SELF_ALIGNMENT_OPTIONS],
          defaultValue: undefined,
          isNullable: true,
        },
      ],
    },

    // ── Appearance ────────────────────────────────────────────────────
    {
      id: 'appearance',
      title: 'Appearance',
      defaultExpanded: false,
      fields: [
        {
          path: 'layout.background.kind',
          guideKey: 'screenedit-flexible-sections-background-type',
          label: 'Background type',
          type: 'select',
          options: [
            { value: 'solid', label: 'Solid Color' },
            { value: 'media', label: 'Media (image)' },
          ],
          defaultValue: undefined,
          isNullable: true,
        },
        {
          path: 'layout.background.color',
          guideKey: 'screenedit-flexible-sections-background-color',
          label: 'Background color',
          type: 'color',
          defaultValue: '#ffffff',
          isNullable: true,
        },
        {
          path: 'layout.cornerRadius.uniform',
          guideKey: 'screenedit-flexible-sections-corner-radius',
          label: 'Corner radius',
          type: 'numberCombo',
          options: NUMBER_0_50_OPTIONS,
          defaultValue: 0,
          defaultScrollValue: 0,
          isNullable: true,
        },
        {
          path: 'layout.border.width',
          guideKey: 'screenedit-flexible-sections-border-width',
          label: 'Border width',
          type: 'numberCombo',
          options: NUMBER_0_50_OPTIONS,
          defaultValue: 0,
          defaultScrollValue: 0,
          isNullable: true,
        },
        {
          path: 'layout.border.color',
          guideKey: 'screenedit-flexible-sections-border-color',
          label: 'Border color',
          type: 'color',
          defaultValue: '#000000',
          isNullable: true,
        },
      ],
    },

    // ── Spacing & Alignment ──────────────────────────────────────────
    {
      id: 'spacingAlignment',
      title: 'Spacing & Alignment',
      defaultExpanded: false,
      fields: [
        {
          path: 'layout.alignment',
          guideKey: 'screenedit-flexible-sections-vertical-alignment',
          label: 'Vertical align',
          type: 'select',
          options: [...VERTICAL_ALIGNMENT_OPTIONS],
          defaultValue: 'top',
          isNullable: true,
        },
        {
          path: 'layout.horizontalAlignment',
          guideKey: 'screenedit-flexible-sections-horizontal-alignment',
          label: 'Horizontal align',
          type: 'select',
          options: [...HORIZONTAL_ALIGNMENT_OPTIONS],
          defaultValue: 'start',
          isNullable: true,
        },
      ],
      paddingConfig: {
        basePath: 'layout.padding',
        label: 'Section padding',
      },
      marginConfig: {
        basePath: 'layout.margin',
        label: 'Section margin',
      },
    },
  ];
}

/**
 * Returns the heightValue options appropriate for the current heightKind.
 * - 'fixed' → 0–1000 (step 10)
 * - 'percent' → 0–100
 * - 'auto' → not applicable
 */
export function getHeightValueOptions(heightKind?: string) {
  if (heightKind === 'percent') return NUMBER_0_100_OPTIONS;
  return NUMBER_0_1000_OPTIONS; // fixed or default
}

/**
 * Returns the widthValue options appropriate for the current widthKind.
 * - 'fixed' → 0–1000 (step 10)
 * - 'percent' → 0–100
 * - 'auto' → not applicable
 */
export function getWidthValueOptions(widthKind?: string) {
  if (widthKind === 'percent') return NUMBER_0_100_OPTIONS;
  return NUMBER_0_1000_OPTIONS;
}
